import { useState } from 'react';
import { toast } from 'sonner';
import { Layers, Plus, Trash2 } from 'lucide-react';

import { useCMSContentTypesQuery } from '@/client/api/cms/cms.query';
import {
  useCreateContentTypeMutation,
  useDeleteContentTypeMutation,
  useUpdateContentTypeMutation,
} from '@/client/api/cms/cms.mutation';
import { CMSContentType } from '@/client/types/cms';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ContentTypesPage = () => {
  useBreadcrumbs([
    { label: 'Content', href: '#' },
    { label: 'Content types' },
  ]);

  const { data, isLoading, isError } = useCMSContentTypesQuery(true, true);
  const createMutation = useCreateContentTypeMutation();
  const updateMutation = useUpdateContentTypeMutation();
  const deleteMutation = useDeleteContentTypeMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CMSContentType | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CMSContentType | null>(null);
  const [form, setForm] = useState({ key: '', label: '', description: '', sort_order: 100 });

  const types = data ?? [];

  const openCreate = () => {
    setEditing(null);
    setForm({ key: '', label: '', description: '', sort_order: 100 });
    setOpen(true);
  };

  const openEdit = (t: CMSContentType) => {
    setEditing(t);
    setForm({
      key: t.key,
      label: t.label,
      description: t.description ?? '',
      sort_order: t.sort_order ?? 100,
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.key.trim() || !form.label.trim()) {
      toast.error('Key and label are required');
      return;
    }
    try {
      if (editing?.cctid) {
        await updateMutation.mutateAsync({
          cctid: editing.cctid,
          label: form.label,
          description: form.description || null,
          sort_order: form.sort_order,
        });
        toast.success('Content type updated');
      } else {
        await createMutation.mutateAsync({
          key: form.key.trim(),
          label: form.label.trim(),
          description: form.description || null,
          sort_order: form.sort_order,
        });
        toast.success('Content type created');
      }
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'Save failed');
    }
  };

  const handleToggleActive = async (t: CMSContentType) => {
    if (!t.cctid) {
      toast.error('Built-in types cannot be edited until migration 0251 is applied');
      return;
    }
    try {
      await updateMutation.mutateAsync({ cctid: t.cctid, active: t.active === false });
      toast.success(t.active === false ? 'Activated' : 'Deactivated');
    } catch (e: any) {
      toast.error(e?.message ?? 'Update failed');
    }
  };

  const handleDelete = (t: CMSContentType) => {
    if (!t.cctid) return;
    if (t.protected) {
      toast.error('Protected types cannot be deleted — deactivate instead');
      return;
    }
    setDeleteTarget(t);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.cctid) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.cctid);
      toast.success('Deleted');
      setDeleteTarget(null);
    } catch (e: any) {
      toast.error(e?.message ?? 'Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="mb-1 flex items-center gap-3">
            <Layers className="h-7 w-7 text-blue-700" />
            <h1 className="text-2xl font-bold text-gray-900">Content types</h1>
          </div>
          <p className="ml-10 text-sm text-gray-500">
            Catalog of `ctype` values used by CMS pages.
            {isError && ' (Showing fallback list — subsystem migration may be missing.)'}
          </p>
        </div>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          New type
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Key', 'Label', 'Status', 'Protected', ''].map((h) => (
                <th key={h || 'a'} className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {[...Array(5)].map((__, j) => (
                    <td key={j} className="px-6 py-4">
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))}
            {!isLoading &&
              types.map((t) => (
                <tr key={t.cctid ?? t.key} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-mono text-sm">{t.key}</td>
                  <td className="px-6 py-4 text-sm">
                    <button className="font-medium text-gray-900 hover:text-blue-700" onClick={() => openEdit(t)}>
                      {t.label}
                    </button>
                    {t.description && <p className="text-xs text-gray-500">{t.description}</p>}
                  </td>
                  <td className="px-6 py-4 text-xs">
                    {t.active === false ? (
                      <span className="text-amber-700">Inactive</span>
                    ) : (
                      <span className="text-emerald-700">Active</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-500">{t.protected ? 'Yes' : '—'}</td>
                  <td className="space-x-2 px-6 py-4 text-right">
                    <Button size="sm" variant="outline" onClick={() => handleToggleActive(t)}>
                      {t.active === false ? 'Activate' : 'Deactivate'}
                    </Button>
                    {!t.protected && t.cctid && (
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(t)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit content type' : 'New content type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Key</Label>
              <Input
                value={form.key}
                disabled={!!editing}
                onChange={(e) => setForm((p) => ({ ...p, key: e.target.value.replace(/\s+/g, '_').toLowerCase() }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Label</Label>
              <Input value={form.label} onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Sort order</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value) }))}
              />
            </div>
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete content type</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes “{deleteTarget?.label}”. Pages using this type may be
              affected. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ContentTypesPage;
